import {
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import * as Sentry from "@sentry/react";
import {
  buildFunctionInvokeError,
  getFunctionInvokeErrorSentryContext,
  getFunctionInvokeErrorSentryFingerprint,
  getFunctionInvokeSentryTitle,
} from "@/lib/functionInvokeErrors";
import { supabase } from "@/integrations/supabase/client";
import { subscribeToPortalSwitchIntent } from "@/lib/portalSwitch";
import { supportsAdminPasskeyFunction } from "@/lib/adminPasskeyConsumers";
export { supportsAdminPasskeyFunction } from "@/lib/adminPasskeyConsumers";

export interface AdminPasskeyStatus {
  eligible: boolean;
  verified: boolean;
  nativeVerified: boolean;
  credentials: Array<{
    id: string;
    name: string;
    createdAt: string;
    lastUsedAt: string | null;
  }>;
}
// Tab-local only: no persistent/shared auth storage, URL, analytics or shared-client headers.
let proof: {
  token: string;
  sessionId: string;
  userId: string;
  expiresAt: number;
} | null = null;
let generation = 0;
let activeCeremonies = 0;
export const isAdminPasskeyCeremonyActive = () => activeCeremonies > 0;
async function withAdminCeremony<T>(
  operation: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  if (signal?.aborted)
    throw new DOMException("Verification cancelled", "AbortError");
  const cancel = () => WebAuthnAbortService.cancelCeremony();
  activeCeremonies++;
  signal?.addEventListener("abort", cancel, { once: true });
  try {
    return await operation();
  } finally {
    activeCeremonies--;
    signal?.removeEventListener("abort", cancel);
  }
}
let expiryTimer: ReturnType<typeof setTimeout> | undefined;
export function clearAdminPasskeyProof() {
  proof = null;
  generation++;
  clearTimeout(expiryTimer);
  if (activeCeremonies) WebAuthnAbortService.cancelCeremony();
}
function sessionIdentity(token: string): string {
  try {
    return (
      JSON.parse(
        atob(token.split(".")[1].replaceAll("-", "+").replaceAll("_", "/")),
      ).session_id ?? ""
    );
  } catch {
    return "";
  }
}
let listenersInstalled = false;
function installAdminListeners() {
  if (listenersInstalled) return;
  listenersInstalled = true;
  supabase.auth.onAuthStateChange((event, session) => {
    if (
      event === "SIGNED_OUT" ||
      !session ||
      (proof &&
        (proof.userId !== session.user.id ||
          proof.sessionId !== sessionIdentity(session.access_token)))
    )
      clearAdminPasskeyProof();
  });
  subscribeToPortalSwitchIntent((intent) => {
    if (intent && intent.target !== "admin") clearAdminPasskeyProof();
  });
}
export async function adminPasskeyHeaders(): Promise<Record<string, string>> {
  if (!proof) return {};
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session) {
    clearAdminPasskeyProof();
    return {};
  }
  if (
    proof &&
    (proof.expiresAt <= Date.now() ||
      proof.userId !== data.session.user.id ||
      proof.sessionId !== sessionIdentity(data.session.access_token))
  )
    clearAdminPasskeyProof();
  return proof ? { "x-admin-passkey-proof": proof.token } : {};
}
export async function adminPasskeyRequest<T>(
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session)
    throw new Error("Sign in to the admin console first.");
  const { data: result, error: invokeError } = await supabase.functions.invoke(
    "admin-passkeys",
    {
      body,
      headers: await adminPasskeyHeaders(),
      signal,
    },
  );
  if (invokeError) {
    const safeError = await buildFunctionInvokeError(
      "admin-passkeys",
      invokeError,
      "Unable to complete admin passkey verification.",
    );
    if (!safeError.isAbort) {
      const context = await getFunctionInvokeErrorSentryContext(safeError);
      const fingerprint =
        await getFunctionInvokeErrorSentryFingerprint(safeError);
      Sentry.captureException(
        new Error(getFunctionInvokeSentryTitle(safeError, safeError.message)),
        {
          ...context,
          fingerprint,
          level:
            safeError.status && safeError.status < 500 ? "warning" : "error",
        },
      );
    }
    throw safeError;
  }
  if (body.action === "remove")
    window.dispatchEvent(new Event("admin-passkey-proof-changed"));
  return result as T;
}
export const getAdminPasskeyStatus = (signal?: AbortSignal) =>
  adminPasskeyRequest<AdminPasskeyStatus>({ action: "status" }, signal);
export function verifyAdminPasskey(signal?: AbortSignal) {
  return withAdminCeremony(() => performAdminVerification(signal), signal);
}
async function performAdminVerification(signal?: AbortSignal) {
  installAdminListeners();
  const started = generation;
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  if (!session) throw new Error("Sign in to the admin console first.");
  const { challengeId, options } = await adminPasskeyRequest<{
    challengeId: string;
    options: PublicKeyCredentialRequestOptionsJSON;
  }>({ action: "authenticate_options" }, signal);
  const response = await startAuthentication({ optionsJSON: options });
  if (signal?.aborted || started !== generation)
    throw new DOMException("Verification cancelled", "AbortError");
  const result = await adminPasskeyRequest<{
    proofToken: string;
    expiresAt: string;
  }>({ action: "authenticate_verify", challengeId, response }, signal);
  const current = (await supabase.auth.getSession()).data.session;
  if (
    signal?.aborted ||
    started !== generation ||
    !current ||
    current.user.id !== session.user.id ||
    sessionIdentity(current.access_token) !==
      sessionIdentity(session.access_token)
  )
    throw new DOMException("Verification cancelled", "AbortError");
  if (
    typeof result.proofToken !== "string" ||
    !/^[A-Za-z0-9_-]{43}$/.test(result.proofToken) ||
    !Number.isFinite(Date.parse(result.expiresAt)) ||
    Date.parse(result.expiresAt) <= Date.now()
  )
    throw new Error("Invalid admin verification response.");
  clearTimeout(expiryTimer);
  expiryTimer = setTimeout(
    () => {
      clearAdminPasskeyProof();
      window.dispatchEvent(new Event("admin-passkey-proof-changed"));
    },
    Math.max(0, Date.parse(result.expiresAt) - Date.now()),
  );
  proof = {
    token: result.proofToken,
    sessionId: sessionIdentity(session.access_token),
    userId: session.user.id,
    expiresAt: Date.parse(result.expiresAt),
  };
}
export function enrollAdminPasskey(name: string, signal?: AbortSignal) {
  return withAdminCeremony(() => performAdminEnrollment(name, signal), signal);
}
async function performAdminEnrollment(name: string, signal?: AbortSignal) {
  installAdminListeners();
  const started = generation;
  const { challengeId, options } = await adminPasskeyRequest<{
    challengeId: string;
    options: PublicKeyCredentialCreationOptionsJSON;
  }>({ action: "register_options" }, signal);
  const response = await startRegistration({ optionsJSON: options });
  if (signal?.aborted || started !== generation)
    throw new DOMException("Enrollment cancelled", "AbortError");
  await adminPasskeyRequest(
    { action: "register_verify", challengeId, response, name },
    signal,
  );
}

// Explicitly admin-scoped callers opt in; never install headers on the shared Supabase client.
export async function invokeAdminFunction<T>(
  name: `admin-${string}`,
  options: NonNullable<Parameters<typeof supabase.functions.invoke>[1]> = {},
) {
  if (!supportsAdminPasskeyFunction(name))
    throw new Error("Unsupported admin passkey consumer");
  // Preserve the existing native call exactly, including synchronous dispatch.
  // orchestration-invoke-allowlist-dynamic-ok: supportsAdminPasskeyFunction restricts this to registered admin consumers.
  if (!proof) return supabase.functions.invoke<T>(name, options);
  const headers = await adminPasskeyHeaders();
  // orchestration-invoke-allowlist-dynamic-ok: the same checked admin consumer receives the session-bound proof.
  return supabase.functions.invoke<T>(name, {
    ...options,
    ...(Object.keys(headers).length > 0
      ? { headers: { ...options.headers, ...headers } }
      : {}),
  });
}
